import { useLenis } from '@/components/lenis-provider'
import type Lenis from 'lenis'
import { useEffect, useRef } from 'react'

/** Time on page after which the modal may open (ms). */
export const LANDING_LEAD_MODAL_MIN_DWELL_MS = 5_000

/** Scroll depth (0–1 of scrollable range) that may open the modal. */
export const LANDING_LEAD_MODAL_MIN_SCROLL_RATIO = 0.2

export const LANDING_LEAD_MODAL_SESSION_KEYS = {
  dismissed: 'pelgo_wp_lead_modal_dismissed',
  submitted: 'pelgo_wp_lead_modal_submitted',
} as const

function isSessionBlocked(): boolean {
  try {
    return (
      sessionStorage.getItem(LANDING_LEAD_MODAL_SESSION_KEYS.dismissed) === '1' ||
      sessionStorage.getItem(LANDING_LEAD_MODAL_SESSION_KEYS.submitted) === '1'
    )
  } catch {
    return false
  }
}

function scrollDepthRatio(lenis: Lenis | null): number {
  if (lenis && lenis.limit > 0) {
    return Math.min(1, Math.max(0, lenis.scroll / lenis.limit))
  }
  const el = document.documentElement
  const scrollRange = el.scrollHeight - window.innerHeight
  if (scrollRange <= 0) return 1
  return Math.min(1, Math.max(0, window.scrollY / scrollRange))
}

/**
 * Opens the lead modal once when the user has been on the page at least
 * {@link LANDING_LEAD_MODAL_MIN_DWELL_MS} or has scrolled at least
 * {@link LANDING_LEAD_MODAL_MIN_SCROLL_RATIO} of the page (whichever comes first).
 * Skips if the session already dismissed or submitted the form.
 *
 * Uses Lenis scroll metrics when available (Pelgo landing smooth scroll), otherwise
 * the window scroll position.
 */
export function useLandingLeadModalTrigger(onOpen: () => void) {
  const lenis = useLenis()
  const firedRef = useRef(false)
  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isSessionBlocked()) return

    const mountTime = Date.now()

    function maybeFire() {
      if (firedRef.current) return
      if (isSessionBlocked()) return

      const dwell = Date.now() - mountTime
      const depth = scrollDepthRatio(lenis)
      if (
        dwell >= LANDING_LEAD_MODAL_MIN_DWELL_MS ||
        depth >= LANDING_LEAD_MODAL_MIN_SCROLL_RATIO
      ) {
        firedRef.current = true
        window.clearTimeout(dwellTimeoutId)
        onOpenRef.current()
      }
    }

    const dwellTimeoutId = window.setTimeout(maybeFire, LANDING_LEAD_MODAL_MIN_DWELL_MS)
    window.addEventListener('scroll', maybeFire, { passive: true })
    window.addEventListener('resize', maybeFire, { passive: true })
    const unsubLenis = lenis ? lenis.on('scroll', maybeFire) : () => undefined
    queueMicrotask(maybeFire)

    return () => {
      window.clearTimeout(dwellTimeoutId)
      window.removeEventListener('scroll', maybeFire)
      window.removeEventListener('resize', maybeFire)
      unsubLenis()
    }
  }, [lenis])
}
